import apiClient, { toAbsoluteBackendUrl } from './apiClient'; // Import the existing apiClient
import { cacheUtil, getApiKey, getTeamEmail, getToken } from '../Utility/Utility';

const buildSessionCacheKey = () => {
  const tokenPart = getToken() || '';
  const apiKeyPart = getApiKey() || '';
  const teamEmailPart = getTeamEmail() || '';
  return `customer-session:${tokenPart}:${apiKeyPart}:${teamEmailPart}`;
};

const SESSION_CACHE_TTL = 10 * 60 * 1000;

const customerService = {
 
  getCustomerByKey: async (key) => {
    try {
      const response = await apiClient.get(`/customers/key/${key}`);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch customer');
    }
  },
  getCustomerById: async (id) => {
    try {
      const response = await apiClient.get(`/customers/id/${id}`);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch customer');
    }
  },

  getAllCustomers: async (state) => {
    try {
      const response = await apiClient.get(`/customers`, {
        params: { state }
      });
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch customers');
    }
  },

  getAllServices: async (filters = {}) => {
    try {
      const response = await apiClient.get('/customer-services', { params: filters });
      return response; // The backend returns { success: true, data: [...], paidTicketValue: ... }
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch services');
    }
  },

  getRenewals: async ({ page = 1, limit = 10, filterType, startDate, endDate, companyName, productName, feedback, usage, status, phone, showAll } = {}) => {
    try {
      const response = await apiClient.get('/customer-services/renewals', {
        params: {
          page,
          limit,
          filterType,
          startDate: startDate ? startDate.toISOString() : undefined,
          endDate: endDate ? endDate.toISOString() : undefined,
          companyName,
          productName,
          feedback,
          usage,
          status,
          phone,
          showAll
        }
      });
      return response; 
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch renewals');
    }
  },

  getCustomerServices: async (customerId) => {
    try {
      const response = await apiClient.get(`/subscriptions`, { params: { customer_id: customerId, limit: 5000, page: 1 } });
      const list = (response?.data || response?.subscriptions || []).map(sub => {
        const product = sub.product || sub.productData || {};
        const alias = sub.custom_alias || sub.alias || '';
        const productName = sub.product_name || product.productName || product.name || '';
        const displayName = alias || productName || 'Unnamed Service';
        return {
          ...sub,
          _id: sub._id,
          id: sub._id || sub.id,
          productName: displayName,
          displayProductName: productName,
          custom_alias: alias,
          service_alias: alias,
          serviceDetails: sub.service_details || sub.serviceDetails || '',
          tenure: sub.tenureMonths || sub.tenure || 12,
          saleDate: sub.sales_date || sub.saleDate || sub.createdAt,
          startDate: sub.start_date || sub.startDate,
          expiryDate: sub.next_renewal_date || sub.expiryDate || sub.renewalDate,
          next_renewal_date: sub.next_renewal_date || sub.expiryDate,
          productId: sub.product_id || sub.productId || product._id,
          totalAmount: sub.amount || sub.totalAmount || 0,
          paidTickets: sub.paidTickets || 0,
          freeTickets: sub.freeTickets || 0,
          isExpired: sub.status === 'expired' || (sub.next_renewal_date && new Date(sub.next_renewal_date) < new Date()),
          status: sub.status || 'active',
          quotations: sub.quotations || [],
          quotation_id: sub.quotation_id || ''
        };
      });
      return list;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch customer services');
    }
  },

  getCustomerQuotations: async (customerId) => {
    try {
      // Use the quotations endpoint with a filter for customerId
      const state = btoa(JSON.stringify({
        filters: { customerId },
        page: 0,
        rowsPerPage: 100, // Fetch more for the detail page
        sortBy: 'quotationDate',
        sortOrder: 'desc'
      }));
      const response = await apiClient.get(`/quotations?state=${encodeURIComponent(state)}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch customer quotations');
    }
  },

  getCustomerInvoices: async (customerId) => {
    try {
      const response = await apiClient.get(`/invoices/customer/${customerId}`);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch customer invoices');
    }
  },

  getCustomerTransactions: async (customerId) => {
    try {
      const response = await apiClient.get(`/transactions/customer/${customerId}`);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch customer transactions');
    }
  },

  getSession: async () => {
    try {
      const cacheKey = buildSessionCacheKey();
      const cached = cacheUtil.get(cacheKey);
      if (cached) {
        return cached;
      }

      const response = await apiClient.get('/get-session');
      if (response?.success) {
        cacheUtil.set(cacheKey, response, SESSION_CACHE_TTL);
      }
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch session');
    }
  },

  getAllMembers: async (userId) => {
    try {
      const response = await apiClient.get(`/register/GetAllMembersByUserId?id=${userId}`);
      const rawMembers = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.ActiveMembers)
          ? response.data.ActiveMembers
          : [];

      const normalizedMembers = rawMembers.map((member) => ({
        id: member.id || member._id || member.email,
        _id: member._id || member.id || member.email,
        username: member.username || member.email,
        email: member.email || member.username,
        name: member.name || member.username || member.email,
        label: member.label || ((member.name || member.username || member.email) === (member.email || member.username)
          ? (member.email || member.username)
          : `${member.name || member.username || member.email} (${member.email || member.username})`),
        role: member.role || ''
      }));

      return {
        ...response,
        data: {
          ActiveMembers: normalizedMembers,
          teams_membership: normalizedMembers
        }
      };
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch team members');
    }
  },

  createCustomer: async (customerData) => {
    try {
      const response = await apiClient.post('/customers', customerData);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to create customer');
    }
  },

  updateCustomer: async (id, customerData) => {
    try {
      const response = await apiClient.put(`/customers/${id}`, customerData);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to update customer');
    }
  },

  updateCustomerService: async (id, serviceData) => {
    try {
      const response = await apiClient.put(`/customer-services/${id}`, serviceData);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to update customer service');
    }
  },

  getCustomerServiceLogs: async (serviceId) => {
    try {
      const response = await apiClient.get(`/customer-services/logs/${serviceId}`);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch service logs');
    }
  },

  uploadFile: async (formData) => {
    try {
      const response = await apiClient.post('/action/uploadAssets', formData);
      return response;
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message || 'Failed to upload file');
    }
  },

  downloadAsset: async (assetId) => {
    try {
      const blob = await apiClient.get(`/action/assets/${assetId}`, {
        responseType: 'blob'
      });
      return blob;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to download asset');
    }
  },

  createCustomerService: async (serviceData) => {
    try {
      const response = await apiClient.post('/customer-services', serviceData);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to create customer service');
    }
  },

  createFollowup: async (followupData) => {
    try {
      const response = await apiClient.post('/followups', followupData);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to create followup');
    }
  },

  getFollowups: async (filters = {}) => {
    try {
      const response = await apiClient.get('/followups', { params: filters });
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch followups');
    }
  },

  updateFollowupStatus: async (id, status, comment) => {
    try {
      const response = await apiClient.put(`/followups/${id}`, { 
        status, 
        completionComment: comment, 
        completedAt: new Date() 
      });
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to update followup status');
    }
  },

  getPaymentHistory: async (params = {}) => {
    try {
      const response = await apiClient.get('/payments', { params });
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch payment history');
    }
  },

  getSubscriberActivityStats: async (params = {}) => {
    try {
      const response = await apiClient.get('/customer-services/subscriber-activity/stats', { params });
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch subscriber activity stats');
    }
  },

  getSubscriberActivityData: async (params = {}) => {
    try {
      const response = await apiClient.get('/customer-services/subscriber-activity/data', { params });
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch subscriber activity data');
    }
  },

  addPayment: async (paymentData) => {
    try {
      const response = await apiClient.post('/payments', paymentData);
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to add payment');
    }
  },

  getMonthlyStats: async () => {
    try {
      const response = await apiClient.get('/customer-services/stats/monthly');
      return response;
    } catch (error) {
      throw new Error(error.response?.message || error.message || 'Failed to fetch monthly stats');
    }
  },

  resolveAttachmentUrl: (attachment) => {
    if (!attachment) return '';
    if (typeof attachment === 'string') return toAbsoluteBackendUrl(attachment);
    return toAbsoluteBackendUrl(attachment.s3Location || attachment.localPath || attachment.url || '');
  },

  downloadAttachment: async (attachment) => {
    if (!attachment) {
      alert('No attachment to download');
      return;
    }
    const assetId = typeof attachment === 'string' ? null : (attachment._id || attachment.id || null);
    const fileName = typeof attachment === 'string'
      ? (attachment.split('/').pop() || 'attachment')
      : (attachment.fileName || attachment.file_name || (attachment.url || '').split('/').pop() || 'attachment');
    try {
      if (assetId) {
        const blob = await customerService.downloadAsset(assetId);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        return;
      }
      const url = customerService.resolveAttachmentUrl(attachment);
      if (!url) {
        alert('Attachment URL missing');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      const fallback = customerService.resolveAttachmentUrl(attachment);
      if (fallback) {
        window.open(fallback, '_blank', 'noopener,noreferrer');
      } else {
        alert(`Download failed: ${error.message || error}`);
      }
    }
  },
};

export default customerService;
